/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, Box, ClickAwayListener, Fade, IconButton, Paper, Popper } from '@mui/material';
import type { UINotification } from '@poweralpha/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import usePANavigate from '../react-components/navigate/usePANavigate';
import NotificationPopperContent from './components/NotificationPopperContent';
import { getFloatingSurfaceSx } from './components/styles';
import { useOptionalNotificationCenter } from './NotificationCenterContext';
import { useNotificationsFeed } from './useNotificationsFeed';
import useNotificationToasts from './useNotificationToasts';
import { useOpenNotification } from './useOpenNotification';

function NotificationCenter() {
  const { location } = usePANavigate();
  const notificationCenter = useOptionalNotificationCenter();
  const openNotification = useOpenNotification();
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const notifToClearRef = useRef<string | null>(null);
  const notifToClearId = new URLSearchParams(location.search).get('clear');
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, markAsViewed, markAllAsViewed } =
    useNotificationsFeed();

  const close = useCallback(() => setIsOpen(false), []);

  const handleNotifClick = useCallback(
    (notification: UINotification, redirect = true) => {
      if (!notification.viewed_at) markAsViewed(notification.id);
      close();
      if (redirect) openNotification(notification);
    },
    [close, markAsViewed, openNotification],
  );

  const handleMarkRead = useCallback(
    (notification: UINotification) => markAsViewed(notification.id),
    [markAsViewed],
  );

  const handleOpenCenter = useCallback(() => {
    close();
    notificationCenter?.openNotificationCenter();
  }, [close, notificationCenter]);

  useNotificationToasts(data?.pages.at(0)?.unread, handleNotifClick, handleOpenCenter);

  useEffect(() => {
    if (notifToClearId === notifToClearRef.current) return;
    if (notifToClearId) markAsViewed(notifToClearId);
    notifToClearRef.current = notifToClearId;
  }, [markAsViewed, notifToClearId]);

  const unreadCount = data?.pages.at(0)?.unread.length;

  return (
    <ClickAwayListener onClickAway={close}>
      <Box>
        <Badge badgeContent={unreadCount} color="error" overlap="circular" max={99}>
          <IconButton
            onClick={() => setIsOpen((prev) => !prev)}
            ref={anchorEl}
            color="secondary"
            size="small"
            aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            aria-expanded={isOpen}
          >
            <NotificationsIcon />
          </IconButton>
        </Badge>
        <Popper
          open={isOpen}
          placement="bottom-end"
          anchorEl={anchorEl.current}
          transition
          sx={{ zIndex: 1500 }}
          onClick={(e) => e.stopPropagation()}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper elevation={0} sx={[{ mt: 1.25 }, getFloatingSurfaceSx]}>
                <NotificationPopperContent
                  data={data}
                  isLoading={isLoading}
                  isFetchingNextPage={isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                  onSelect={handleNotifClick}
                  onMarkRead={handleMarkRead}
                  onMarkAllRead={markAllAsViewed}
                  onOpenCenter={handleOpenCenter}
                />
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default NotificationCenter;
